import { useState, useEffect } from 'react';
import { apiFetch, getCampaignId } from '../api/client.js';
import AppointmentCalendar from './AppointmentCalendar.jsx';

const STATUS_STYLES = {
  scheduled: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  no_show: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800',
};

const STATUS_LABELS = {
  scheduled: 'Planifié',
  completed: 'Terminé',
  no_show: 'Absent',
  cancelled: 'Annulé',
};

/**
 * List of control appointments with status filter tabs and calendar view.
 */
export default function AppointmentList() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [weekOffset, setWeekOffset] = useState(0);

  useEffect(() => {
    loadAppointments();
  }, [filter]);

  const loadAppointments = async () => {
    setLoading(true);
    setError('');
    try {
      const cid = getCampaignId();
      const qs = filter ? `?status=${filter}` : '';
      const data = await apiFetch(`/c/${cid}/appointments${qs}`);
      setAppointments(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const weekStart = getOffsetMonday(weekOffset);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Rendez-vous controle</h2>
        <button
          onClick={() => setViewMode(viewMode === 'list' ? 'calendar' : 'list')}
          className="bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm"
        >
          {viewMode === 'list' ? 'Calendrier' : 'Liste'}
        </button>
      </div>

      {/* Filter tabs (list mode only) */}
      {viewMode === 'list' && (
        <div className="flex gap-2 mb-4 overflow-x-auto">
          {[
            { key: '', label: 'Tous' },
            { key: 'scheduled', label: 'Planifiés' },
            { key: 'completed', label: 'Terminés' },
            { key: 'no_show', label: 'Absents' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
                filter === tab.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Week navigation (calendar mode) */}
      {viewMode === 'calendar' && (
        <div className="flex justify-between items-center mb-3">
          <button onClick={() => setWeekOffset(weekOffset - 1)} className="text-sm text-blue-600">
            &larr; Sem. prec.
          </button>
          <span className="text-sm font-medium">{formatWeekRange(weekStart)}</span>
          <button onClick={() => setWeekOffset(weekOffset + 1)} className="text-sm text-blue-600">
            Sem. suiv. &rarr;
          </button>
        </div>
      )}

      {error && <p className="text-red-600 text-sm mb-3">{error}</p>}

      {loading ? (
        <p className="text-gray-500 text-center py-8">Chargement...</p>
      ) : viewMode === 'calendar' ? (
        <AppointmentCalendar appointments={appointments} weekStart={weekStart} />
      ) : appointments.length === 0 ? (
        <p className="text-gray-400 text-center py-8">Aucun rendez-vous.</p>
      ) : (
        <div className="space-y-2">
          {appointments.map((apt) => (
            <div key={apt.id} className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex justify-between items-start mb-2">
                <span className="font-medium">{apt.client_name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLES[apt.status] || 'bg-gray-100'}`}>
                  {STATUS_LABELS[apt.status] || apt.status}
                </span>
              </div>
              <p className="text-sm text-gray-500">{apt.client_address}</p>
              {apt.scheduled_at && (
                <p className="text-sm text-gray-600 mt-1">
                  {new Date(apt.scheduled_at).toLocaleString('fr-FR')}
                </p>
              )}
              {apt.notes && <p className="text-xs text-gray-400 mt-1">{apt.notes}</p>}
              <div className="text-xs text-gray-400 mt-2">
                {apt.booking_method === 'calendly' ? 'Via Calendly' : 'Manuel'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Get Monday date string offset by N weeks from current week.
 * @param {number} offset
 * @returns {string} YYYY-MM-DD
 */
function getOffsetMonday(offset) {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1) + offset * 7;
  now.setDate(diff);
  return now.toISOString().split('T')[0];
}

/**
 * Format a week range label from a Monday date string.
 * @param {string} mondayStr
 * @returns {string}
 */
function formatWeekRange(mondayStr) {
  const mon = new Date(mondayStr);
  const sun = new Date(mon);
  sun.setDate(sun.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  return `${fmt(mon)} - ${fmt(sun)}`;
}
